import React from 'react';
import { Size } from '../../../core/enums/Size';
import { isMobileScreenSize } from '../../../core/utils/isMobileScreenSize';

import './Input.scss';

export type InputMode = 'search' | 'text' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal' | undefined;

interface Props {
  label?: string;
  id: string;
  name: string;
  type: string;
  error?: string;
  errorTextVisible?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value: string | number;
  labelSize?: Size;
  formatting?: (value: string | number) => string | number | undefined;
  className?: string;
  placeholder?: string;
  required?: boolean;
  infoText?: string;
  focused?: boolean;
  disabled?: boolean;
  inputMode?: InputMode;
}

const Input: React.FC<Props> = ({
  label,
  error,
  id,
  name,
  type,
  onChange,
  onBlur,
  value,
  labelSize,
  formatting,
  className,
  errorTextVisible = true,
  placeholder,
  required,
  focused,
  disabled,
  infoText,
  inputMode
}) => {
  const [isFocused, setFocused] = React.useState(false);
  const inputEl = React.useRef<HTMLInputElement>(null);
  const isMobileScreen = isMobileScreenSize();

  React.useEffect(() => {
    if (focused) {
      inputEl.current?.focus();
    }
  }, [inputEl, focused]);

  return (
    <div className={`input ${error ? 'input--error' : ''} ${isMobileScreen && 'input--mobile'} ${className ?? ''}`}>
      {label && (
        <label className={`input__label ${labelSize ? `input__label--${labelSize}` : ''} ${disabled ? 'input__label--disabled' : ''}`} htmlFor={id}>
          {label}
          {required && '* '}
          {infoText && <span className="font-weight-light">{infoText}</span>}
        </label>
      )}
      <input
        type={type}
        className="input__input"
        inputMode={inputMode}
        ref={inputEl}
        disabled={disabled}
        value={!isFocused && formatting ? formatting(value) : value}
        id={id}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={(e) => {
          setFocused(false);
          if (onBlur) {
            onBlur(e);
          }
        }}
        required={required}
      />
      {errorTextVisible && error && <div className="input__error">{error}</div>}
    </div>
  );
};

export default Input;
